// Defined Task Classification Dialogue Blueprint
// This file defines the static statechart for well-defined tasks that have
// standardized workflows and don't need the complex variant/amount/location steps

export type DefinedTaskDialogueState =
  | 'subtasks_selection' // Step 1: User selects subtasks for the well-defined task
  | 'subtasks_processing' // Step 2: Process each selected subtask (leaf validation, variants, children, amount)
  | 'defined_task_completed' // Terminal: all subtasks done; work location comes from the assignment (opdracht)
  | 'defined_task_aborted' // Terminal: Defined task processing aborted

// Typed triggers for compiler-checked transitions
export type DefinedTaskTrigger =
  | 'SELECT_SUBTASKS' // User selects subtasks → subtasks_processing
  | 'ALL_SUBTASKS_PROCESSED' // All subtasks completed → terminal (location from assignment, not user)
  | 'CONFIRM' // User confirms action
  | 'ABORT' // User wants to abort
  | 'SOMETHING_ELSE' // User says something else
  | 'DEFINED_TASK_COMPLETED' // Parent-facing terminal trigger
  | 'DEFINED_TASK_ABORTED' // Parent-facing terminal trigger

// State metadata interface
export interface DefinedTaskStateMetadata {
  requiredSlots?: string[];
  entryEffectKey?: string;
  isTerminal?: boolean;
  entryActionKeys?: string[];
}

// Pure static statechart - no actions or I/O
export const DEFINED_TASK_STATE_TRANSITIONS: Record<
  DefinedTaskDialogueState,
  Partial<Record<DefinedTaskTrigger, DefinedTaskDialogueState>>
> = {
  subtasks_selection: {
    SELECT_SUBTASKS: 'subtasks_processing',
    ABORT: 'defined_task_aborted',
    SOMETHING_ELSE: 'defined_task_aborted',
  },

  subtasks_processing: {
    ALL_SUBTASKS_PROCESSED: 'defined_task_completed',
    ABORT: 'defined_task_aborted',
    SOMETHING_ELSE: 'defined_task_aborted',
  },

  defined_task_completed: {
    // Terminal state - no transitions
  },

  defined_task_aborted: {
    // Terminal state - no transitions
  },
};

// State metadata
export const DEFINED_TASK_STATE_METADATA: Record<DefinedTaskDialogueState, DefinedTaskStateMetadata> = {
  subtasks_selection: {
    requiredSlots: [],
    entryActionKeys: ['show_defined_task_message'],
  },
  subtasks_processing: {
    requiredSlots: [],
    entryActionKeys: ['start_subtask_processing'],
  },
  defined_task_completed: {
    isTerminal: true,
  },
  defined_task_aborted: {
    isTerminal: true,
  },
};

// Pure helper functions - no side effects
export function getNextDefinedTaskState(
  currentState: DefinedTaskDialogueState,
  trigger: DefinedTaskTrigger
): DefinedTaskDialogueState | null {
  const transitions = DEFINED_TASK_STATE_TRANSITIONS[currentState];
  return transitions[trigger] || null;
}

export function isValidDefinedTaskTransition(
  currentState: DefinedTaskDialogueState,
  trigger: DefinedTaskTrigger
): boolean {
  const nextState = getNextDefinedTaskState(currentState, trigger);
  return nextState !== null && nextState !== currentState;
}

export function getDefinedTaskStateMetadata(state: DefinedTaskDialogueState): DefinedTaskStateMetadata {
  return DEFINED_TASK_STATE_METADATA[state];
}
